import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IconComponent } from '@examdojo/core/icon';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import type {
  AxisConfig,
  AxisOrientation,
  BoundingBoxConfig,
  CoordinateSystemConfig,
  GridType,
  TickLabelFormat,
  TicksConfig,
} from '../../coordinate-system';

interface SelectOption {
  label: string;
  value: string;
}

interface GridTypeOption {
  label: string;
  value: GridType;
}

interface FixedAxisOption {
  label: string;
  value: 'none' | 'x' | 'y';
}

type AxesMode = 'off' | 'default' | 'custom';

@Component({
  selector: 'dojo-coordinate-system-editor',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatSlideToggleModule,
    IconButtonComponent,
    IconComponent,
    NumberInputComponent,
    TextInputComponent,
    SingleSelectComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './coordinate-system-editor.component.html',
})
export class CoordinateSystemEditorComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    effect(() => {
      this.coordSystem();
      this.syncFromConfig();
    });

    this.xStartCtrl.valueChanges
      .pipe(
        tap(() => this.emitBoundingBoxChange()),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.xEndCtrl.valueChanges
      .pipe(
        tap(() => this.emitBoundingBoxChange()),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.yStartCtrl.valueChanges
      .pipe(
        tap(() => this.emitBoundingBoxChange()),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.yEndCtrl.valueChanges
      .pipe(
        tap(() => this.emitBoundingBoxChange()),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.ticksDistanceCtrl.valueChanges
      .pipe(
        tap((val) => this.emitTicksChange({ ticksDistance: val ?? undefined })),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.minorTicksCtrl.valueChanges
      .pipe(
        tap((val) => this.emitTicksChange({ minorTicks: val })),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  // --- Inputs / Outputs ---
  readonly coordSystem = input<CoordinateSystemConfig>();
  readonly coordSystemChange = output<CoordinateSystemConfig>();

  // --- Form controls ---
  readonly xStartCtrl = new FormControl<number>(-10, { nonNullable: true });
  readonly xEndCtrl = new FormControl<number>(10, { nonNullable: true });
  readonly yStartCtrl = new FormControl<number>(-10, { nonNullable: true });
  readonly yEndCtrl = new FormControl<number>(10, { nonNullable: true });
  readonly gridTypeCtrl = new FormControl<GridType>('lines', { nonNullable: true });
  readonly fixedAxisCtrl = new FormControl<'none' | 'x' | 'y'>('none', { nonNullable: true });
  readonly axesModeCtrl = new FormControl<AxesMode>('default', { nonNullable: true });
  readonly ticksVisibleCtrl = new FormControl<boolean>(true, { nonNullable: true });
  readonly ticksDistanceCtrl = new FormControl<number | null>(null);
  readonly drawLabelsCtrl = new FormControl<boolean>(true, { nonNullable: true });
  readonly drawZeroCtrl = new FormControl<boolean>(false, { nonNullable: true });
  readonly minorTicksCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly labelFormatCtrl = new FormControl<string>('default', { nonNullable: true });

  // --- Per-axis controls (lazy) ---
  private readonly axisSelectCtrls = new Map<string, FormControl<string>>();
  private readonly axisNumberCtrls = new Map<string, FormControl<number | null>>();
  private readonly axisTextCtrls = new Map<string, FormControl<string>>();

  // --- Options ---
  readonly gridTypeOptions: GridTypeOption[] = [
    { label: 'Lines', value: 'lines' },
    { label: 'Dots', value: 'dots' },
  ];

  readonly fixedAxisOptions: FixedAxisOption[] = [
    { label: 'Free', value: 'none' },
    { label: 'Fixed X', value: 'x' },
    { label: 'Fixed Y', value: 'y' },
  ];

  readonly labelFormatOptions: SelectOption[] = [
    { label: 'Default', value: 'default' },
    { label: 'Pi (\u03C0)', value: 'pi' },
    { label: 'Degrees (\u00B0)', value: 'degrees' },
  ];

  readonly axesModeOptions: SelectOption[] = [
    { label: 'Off', value: 'off' },
    { label: 'Default (on)', value: 'default' },
    { label: 'Custom', value: 'custom' },
  ];

  readonly axisOrientationOptions: SelectOption[] = [
    { label: 'Horizontal', value: 'horizontal' },
    { label: 'Vertical', value: 'vertical' },
  ];

  // --- Computed signals ---
  readonly gridVisible = computed(() => this.coordSystem()?.grid?.visible !== false);
  readonly axesMode = computed<AxesMode>(() => {
    const axes = this.coordSystem()?.axes;
    if (!axes) {
      return 'default';
    }
    if (axes.visible === false) {
      return 'off';
    }
    if (axes.custom) {
      return 'custom';
    }
    return 'default';
  });
  readonly axesArray = computed<AxisConfig[]>(() => this.coordSystem()?.axes?.custom ?? []);
  readonly ticksVisible = computed(() => this.coordSystem()?.ticks?.visible !== false);

  // --- Per-axis form control getters ---

  getAxisCtrl(idx: number, field: string): FormControl<string> {
    const key = `${idx}-${field}`;
    if (!this.axisSelectCtrls.has(key)) {
      this.axisSelectCtrls.set(key, new FormControl<string>('', { nonNullable: true }));
    }
    return this.axisSelectCtrls.get(key)!;
  }

  getAxisNumberCtrl(idx: number, field: string): FormControl<number | null> {
    const key = `${idx}-${field}`;
    if (!this.axisNumberCtrls.has(key)) {
      const ctrl = new FormControl<number | null>(null);
      this.axisNumberCtrls.set(key, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => {
            if (field === 'position') {
              this.onAxisFieldChange(idx, 'position', val ?? 0);
            } else if (field.startsWith('ticks.')) {
              this.onAxisTicksFieldChange(idx, field.split('.')[1], val ?? undefined);
            } else if (field.startsWith('axisLabel.')) {
              this.onAxisLabelFieldChange(idx, field.split('.')[1], val ?? undefined);
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.axisNumberCtrls.get(key)!;
  }

  getAxisTextCtrl(idx: number, field: string): FormControl<string> {
    const key = `${idx}-${field}`;
    if (!this.axisTextCtrls.has(key)) {
      const ctrl = new FormControl<string>('', { nonNullable: true });
      this.axisTextCtrls.set(key, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => {
            if (field.startsWith('axisLabel.')) {
              this.onAxisLabelFieldChange(idx, field.split('.')[1], val || undefined);
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.axisTextCtrls.get(key)!;
  }

  // --- Change handlers ---

  onGridVisibleChange(visible: boolean): void {
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, grid: { ...(cs.grid ?? {}), visible } });
  }

  onGridTypeChange(type: unknown): void {
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, grid: { ...(cs.grid ?? {}), type: type as GridType } });
  }

  onFixedAxisChange(value: unknown): void {
    const cs = this.coordSystem() ?? {};
    const v = value as 'none' | 'x' | 'y';
    this.coordSystemChange.emit({ ...cs, fixedAxis: v === 'none' ? undefined : v });
  }

  onAxesModeChange(mode: unknown): void {
    const { axes: _axes, ...cs } = this.coordSystem() ?? {};
    const m = mode as AxesMode;
    if (m === 'off') {
      this.coordSystemChange.emit({ ...cs, axes: { visible: false } });
    } else if (m === 'default') {
      this.coordSystemChange.emit(cs);
    } else {
      const defaultAxes: AxisConfig[] = [
        { id: 'x', orientation: 'horizontal', position: 0, isPrimary: true },
        { id: 'y', orientation: 'vertical', position: 0, isPrimary: true },
      ];
      this.coordSystemChange.emit({ ...cs, axes: { custom: defaultAxes } });
    }
  }

  onAxisFieldChange(idx: number, field: string, value: unknown): void {
    const axes = [...this.axesArray()];
    if (idx >= axes.length) {
      return;
    }
    axes[idx] = { ...axes[idx], [field]: value };
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, axes: { custom: axes } });
  }

  onAxisTicksFieldChange(idx: number, field: string, value: unknown): void {
    const axes = [...this.axesArray()];
    if (idx >= axes.length) {
      return;
    }
    axes[idx] = { ...axes[idx], ticks: { ...(axes[idx].ticks ?? {}), [field]: value } };
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, axes: { custom: axes } });
  }

  onAxisLabelFieldChange(idx: number, field: string, value: unknown): void {
    const axes = [...this.axesArray()];
    if (idx >= axes.length) {
      return;
    }
    const existing = axes[idx].axisLabel ?? { text: '' };
    axes[idx] = { ...axes[idx], axisLabel: { ...existing, [field]: value } };
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, axes: { custom: axes } });
  }

  onDeleteAxis(idx: number): void {
    const axes = [...this.axesArray()];
    axes.splice(idx, 1);
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, axes: axes.length ? { custom: axes } : { visible: false } });
  }

  onAddAxis(): void {
    const axes = [...this.axesArray()];
    const existingIds = axes.map((a) => a.id);
    let id = 'a';
    let counter = 1;
    while (existingIds.includes(id)) {
      id = `a${++counter}`;
    }
    const orientation: AxisOrientation = axes.length % 2 === 0 ? 'horizontal' : 'vertical';
    axes.push({ id, orientation, position: 0 });
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, axes: { custom: axes } });
  }

  onTicksVisibleChange(visible: boolean): void {
    this.emitTicksChange({ visible });
  }

  onDrawLabelsChange(enabled: boolean): void {
    this.emitTicksChange({ drawLabels: enabled });
  }

  onDrawZeroChange(enabled: boolean): void {
    this.emitTicksChange({ drawZero: enabled });
  }

  onLabelFormatChange(format: unknown): void {
    this.emitTicksChange({ labelFormat: (format as TickLabelFormat) || undefined });
  }

  // --- Private ---

  private syncFromConfig(): void {
    const cs = this.coordSystem();
    if (cs?.boundingbox) {
      this.xStartCtrl.setValue(cs.boundingbox.xStart, { emitEvent: false });
      this.xEndCtrl.setValue(cs.boundingbox.xEnd, { emitEvent: false });
      this.yStartCtrl.setValue(cs.boundingbox.yStart, { emitEvent: false });
      this.yEndCtrl.setValue(cs.boundingbox.yEnd, { emitEvent: false });
    }
    if (cs?.grid) {
      this.gridTypeCtrl.setValue(cs.grid.type ?? 'lines', { emitEvent: false });
    }
    this.fixedAxisCtrl.setValue(cs?.fixedAxis ?? 'none', { emitEvent: false });
    this.axesModeCtrl.setValue(this.axesMode(), { emitEvent: false });
    this.syncAxesControls();
    this.ticksVisibleCtrl.setValue(cs?.ticks?.visible !== false, { emitEvent: false });
    this.ticksDistanceCtrl.setValue(cs?.ticks?.ticksDistance ?? null, { emitEvent: false });
    this.drawLabelsCtrl.setValue(cs?.ticks?.drawLabels !== false, { emitEvent: false });
    this.drawZeroCtrl.setValue(cs?.ticks?.drawZero === true, { emitEvent: false });
    this.minorTicksCtrl.setValue(cs?.ticks?.minorTicks ?? 0, { emitEvent: false });
    this.labelFormatCtrl.setValue(cs?.ticks?.labelFormat ?? 'default', { emitEvent: false });
  }

  private syncAxesControls(): void {
    const axes = this.axesArray();
    for (let idx = 0; idx < axes.length; idx++) {
      const axis = axes[idx];
      this.getAxisCtrl(idx, 'orientation').setValue(axis.orientation ?? 'horizontal', { emitEvent: false });
      this.getAxisNumberCtrl(idx, 'position').setValue(axis.position ?? 0, { emitEvent: false });
      this.getAxisNumberCtrl(idx, 'ticks.ticksDistance').setValue(axis.ticks?.ticksDistance ?? null, {
        emitEvent: false,
      });
      this.getAxisCtrl(idx, 'ticks.labelFormat').setValue(axis.ticks?.labelFormat ?? 'default', { emitEvent: false });
      this.getAxisTextCtrl(idx, 'axisLabel.text').setValue(axis.axisLabel?.text ?? '', { emitEvent: false });
      this.getAxisNumberCtrl(idx, 'axisLabel.fontSize').setValue(axis.axisLabel?.fontSize ?? null, {
        emitEvent: false,
      });
    }
  }

  private emitTicksChange(partial: Partial<TicksConfig>): void {
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, ticks: { ...cs.ticks, ...partial } });
  }

  private emitBoundingBoxChange(): void {
    const cs = this.coordSystem() ?? {};
    const boundingbox: BoundingBoxConfig = {
      xStart: this.xStartCtrl.value,
      xEnd: this.xEndCtrl.value,
      yStart: this.yStartCtrl.value,
      yEnd: this.yEndCtrl.value,
    };
    this.coordSystemChange.emit({ ...cs, boundingbox });
  }
}
